import { mount } from 'svelte'
import './app.css'
import App from './App.svelte'
import { Capacitor } from '@capacitor/core'
import { CapacitorUpdater } from '@capgo/capacitor-updater'
import { App as CapacitorApp } from '@capacitor/app'

CapacitorUpdater.notifyAppReady()

// Handle Android hardware back button to prevent accidental app closure
const ROOT_PATHS = ['/', '/home', '/login']

CapacitorApp.addListener('backButton', ({ canGoBack }) => {
    const currentPath = window.location.pathname
    if (ROOT_PATHS.includes(currentPath)) {
        // On root pages, minimize the app instead of closing it
        CapacitorApp.minimizeApp()
    } else if (canGoBack) {
        window.history.back()
    } else {
        CapacitorApp.minimizeApp()
    }
})

// Native-only plugin setup
if (Capacitor.isNativePlatform()) {
    // Keyboard handling differs per platform:
    //  - iOS: webview is NOT resized natively (resize "none"); we expose the keyboard
    //    height as a CSS var so .kb-screen lifts the input in one smooth motion.
    //  - Android: the webview resizes natively (resize "native"); the content reflows
    //    above the keyboard on its own, so we must NOT also subtract a CSS offset
    //    (that would double-shrink and leave empty space). Keep --keyboard-height at 0.
    import('@capacitor/keyboard').then(({ Keyboard, KeyboardResize }) => {
        if (Capacitor.getPlatform() === 'ios') {
            Keyboard.setResizeMode({ mode: KeyboardResize.None }).catch(() => {})
            Keyboard.addListener('keyboardWillShow', (info) => {
                document.documentElement.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`)
                window.dispatchEvent(new CustomEvent('keyboard-show', { detail: info }))
            })
            Keyboard.addListener('keyboardWillHide', () => {
                document.documentElement.style.setProperty('--keyboard-height', '0px')
                window.dispatchEvent(new CustomEvent('keyboard-hide'))
            })
        } else {
            Keyboard.setResizeMode({ mode: KeyboardResize.Native }).catch(() => {})
        }
    })

    // Status bar — light style (white icons) for the dark blue header
    import('@capacitor/status-bar').then(({ StatusBar, Style }) => {
        StatusBar.setOverlaysWebView({ overlay: true })
        StatusBar.setStyle({ style: Style.Light })
    })

    // App lifecycle — re-check auth on resume
    CapacitorApp.addListener('appStateChange', async ({ isActive }) => {
        if (!isActive) return
        try {
            const { checkAuth } = await import('@/shared/services/auth.service')
            const result = await checkAuth()
            if (!result.success) {
                window.location.replace('/login')
            }
        } catch (_e) {
            // Network error on resume — ignore, user will see it on next action
        }
    })
}

// Simple app initialization without configuration loading
const app = mount(App, {
    target: document.getElementById('app')
})

export default app
